import { useFormik } from "formik";
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { IHospitalConfiguration } from "../../../../../../@types/Login";
import { Notification } from "../../../../../../@types/Notification";
import { IPortalPreferences } from "../../../../../../@types/Preferences";
import {
  IAddEditStaffRequest,
  IGetStaffResponse,
} from "../../../../../../@types/Staff";
import { Button } from "../../../../../../components/Button";
import { Checkbox } from "../../../../../../components/Checkbox";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../components/SelectDropdown";
import { SkeletonLoading } from "../../../../../../components/Skeleton";
import { TextField } from "../../../../../../components/TextField";
import {
  AUTH_HOSPITAL_CONFIGURATION,
  AUTH_PORTAL_PREFERENCES,
} from "../../../../../../constants/localStorageKeys";
import { usePatientDetailsDropdownItems } from "../../../../../../hooks/queries/patients";
import { useAvailableWards } from "../../../../../../hooks/queries/wards";
import { useWardLabel } from "../../../../../../hooks/useWardLabel";
import { staffService } from "../../../../../../services/staffService";
import { isProduction, isUat } from "../../../../../../utils/environment";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../../../../../utils/storage";
import {
  CancelButton,
  ModalTitle,
} from "../../../SecurityModal/SecurityModal.styles";
import {
  CheckboxContainer,
  ContentContainer,
  ErrorMessage,
  FormField,
  inputCss,
  ModalContainer,
  Row,
  ServerErrorMessage,
  submitButtonCss,
  SubmitContainer,
  reactPhoneInputCss,
} from "./AddEditStaffModal.styles";
import "react-phone-input-2/lib/bootstrap.css";

type AddEditStaffModalProps = {
  staffId?: number;
  content?: IGetStaffResponse;
  onClose: (notification?: Notification, refetchTable?: boolean) => void;
};

export function AddEditStaffModal({
  staffId,
  content,
  onClose,
}: AddEditStaffModalProps) {
  const { t } = useTranslation();

  // Hook provides countries and hospitals data needed elsewhere
  usePatientDetailsDropdownItems({
    patientId: "0",
  });
  const { availableWards, refetchWards } = useAvailableWards();
  const wardLabel = useWardLabel();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [rerender, setRerender] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [phoneWithCountryCode, setPhoneWithCountryCode] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>();

  const emailRef = useRef<HTMLDivElement>(null);
  const confirmEmailRef = useRef<HTMLDivElement>(null);
  const firstNameRef = useRef<HTMLDivElement>(null);
  const lastNameRef = useRef<HTMLDivElement>(null);
  const phoneNumberRef = useRef<HTMLDivElement>(null);
  const permissionLevelRef = useRef<HTMLDivElement>(null);
  const wardRef = useRef<HTMLDivElement>(null);
  const roleRef = useRef<HTMLDivElement>(null);

  // EN-4961: hospitals on the new role model flip the HospitalConfiguration
  // flag, which collapses the form to Role/Scope/Wards-only and drops the
  // legacy is_admin / receive-alerts / archive-measurements checkboxes.
  // Computed once from the login response (AUTH_HOSPITAL_CONFIGURATION) —
  // scoped at hospital-group level, not per-ward.
  const hideLegacyFields = useMemo(() => {
    const hospitalConfig = safeStorage.getItem(AUTH_HOSPITAL_CONFIGURATION);
    const parsed = safeJsonParse<IHospitalConfiguration | undefined>(
      hospitalConfig,
      undefined
    );
    return parsed?.hideLegacyAddStaffCheckboxes ?? false;
  }, []);

  // EN-5096: the API caps the permission level an admin can grant at their own,
  // so only offer scopes the caller is entitled to. Ward-scoped ("hospital")
  // admins — and legacy rows with no level set — may only create ward-scoped
  // staff. Read once from the login payload, same as the country code below.
  const callerPermissionLevel = useMemo(() => {
    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);
    const parsed = safeJsonParse<IPortalPreferences | undefined>(
      portalPreferences,
      undefined
    );
    return parsed?.permissionLevel;
  }, []);
  const canGrantHospitalGroupScope =
    callerPermissionLevel === "hospital-group" ||
    callerPermissionLevel === "hospital-group-hierarchy";

  useEffect(() => {
    refetchWards();
    setIsMounted(true);

    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

    if (portalPreferences) {
      const parsedPortalPreferences = safeJsonParse<
        IPortalPreferences | undefined
      >(portalPreferences, undefined);

      if (parsedPortalPreferences) {
        setCountryCode(parsedPortalPreferences.countryCode?.toLowerCase());
      }
    }

    return () => {
      setIsMounted(false);
    };
  }, []);

  const handleOnSubmitForm = async ({
    email,
    firstName,
    lastName,
    phoneNumber,
    permissionLevel,
    ward,
    receiveAlerts,
    archiveMeasurements,
    activeAccount,
    role,
  }: any) => {
    setIsLoading(true);
    setErrorMessage("");

    // EN-4961: when the new flag is on the modal hides legacy toggles, so we
    // can't trust their formik values for the request. Force them to a safe
    // default and let the API derive is_admin from the Role on the server.
    // EN-5096: the former isAdmin / scope / wardIds mirrors were never read by
    // the API and have been removed from its contract.
    const staffData: IAddEditStaffRequest = {
      email,
      firstName,
      lastName,
      phoneNumber,
      permissionLevel,
      wardId: ward,
      receiveAlerts: hideLegacyFields ? false : receiveAlerts,
      archiveMeasurements: hideLegacyFields ? false : archiveMeasurements,
      activeAccount,
      role,
    };

    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    let refetchTable = false;

    if (staffId !== undefined && content !== undefined) {
      const result = await staffService.editHospitlStaff(staffId, staffData);

      if (result.status >= 200 && result.status < 300) {
        notification.message = t("settings_staffModal_toasts_updated");
        refetchTable = true;
        onClose(notification, refetchTable);
      } else if (result.status === 400 || result.status === 403) {
        setErrorMessage((result.data as any).errors[0].message);
        setIsLoading(false);
        return;
      } else {
        notification.message = t("settings_staffModal_toasts_updateFailed");
        notification.type = "error";
        onClose(notification, refetchTable);
      }
    } else {
      const result = await staffService.addHospitlStaff(staffData);

      if (result.status >= 200 && result.status < 300) {
        notification.message = t("settings_staffModal_toasts_created");
        refetchTable = true;
        onClose(notification, refetchTable);
      } else if (result.status === 400 || result.status === 403) {
        setErrorMessage((result.data as any).errors[0].message);
        setIsLoading(false);
        return;
      } else {
        notification.message = t("settings_staffModal_toasts_createFailed");
        notification.type = "error";
        onClose(notification, refetchTable);
      }
    }

    mixpanelActions.track("User action: Save");
  };

  const basePhoneNumberSchema = yup
    .string()
    .min(8, t("settings_staffModal_validation_phoneInvalid"));

  const formValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("login_validation_emailInvalid"))
      .required(t("login_validation_emailInvalid")),
    confirmEmail: yup
      .string()
      .oneOf([yup.ref("email"), null], t("settings_staff_emailMismatch"))
      .required(t("login_validation_emailInvalid")),
    firstName: yup
      .string()
      .required(t("settings_staffModal_validation_valueRequired")),
    lastName: yup
      .string()
      .required(t("settings_staffModal_validation_valueRequired")),
    role: yup
      .string()
      .required(t("settings_staffModal_validation_selectRequired")),
    permissionLevel: yup
      .string()
      .required(t("settings_staffModal_validation_selectRequired")),
    // Staff are always scoped to a single ward, so the ward dropdown is
    // required whenever the scope is "hospital", regardless of the
    // new-roles flag.
    ward: yup.string().when("permissionLevel", {
      is: "hospital",
      then: yup
        .string()
        .required(t("settings_staffModal_validation_selectRequired")),
    }),
    phoneNumber: yup.string().when("receiveAlerts", {
      is: true,
      then: basePhoneNumberSchema.required(
        t("settings_staffModal_validation_selectRequired")
      ),
      otherwise: basePhoneNumberSchema,
    }),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      confirmEmail: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      permissionLevel: content?.permissionLevel,
      ward: content?.wardId?.toString(),
      receiveAlerts: false,
      archiveMeasurements: false,
      activeAccount: true,
      role: content?.role ?? "",
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  useEffect(() => {
    if (content) {
      setIsLoadingData(true);

      formik.setValues({
        email: content.email,
        confirmEmail: content.email,
        firstName: content.firstName,
        lastName: content.lastName,
        phoneNumber: content.phoneNumber ?? "",
        permissionLevel: content.permissionLevel,
        ward: content.wardId?.toString(),
        receiveAlerts: content.receiveAlerts,
        archiveMeasurements: content.archiveMeasurements,
        activeAccount: content.activeAccount,
        role: content.role ?? "",
      });

      if (content.phoneNumber) {
        setPhoneWithCountryCode(content.phoneNumber);
      }

      formik.validateForm();

      setRerender(true);

      setTimeout(() => {
        setRerender(false);
        setIsLoadingData(false);
      }, 100);
    }
  }, [content]);

  type IErrorField =
    | "email"
    | "confirmEmail"
    | "firstName"
    | "lastName"
    | "permissionLevel"
    | "phoneNumber"
    | "ward"
    | "role";

  const shouldShowErrorMessage = (field: IErrorField) => {
    return formik.touched[field] ? formik.errors[field]?.toString() ?? "" : "";
  };

  const handlePhoneInput = (country: any, formattedValue: string) => {
    const formattedPhoneNumber = formattedValue.replace(/ /g, "");
    const formattedDialCode = `+${country.dialCode}`;

    if (formattedPhoneNumber !== formattedDialCode) {
      formik.setFieldValue("phoneNumber", formattedPhoneNumber);
    } else {
      formik.setFieldValue("phoneNumber", "");
    }

    setPhoneWithCountryCode(formattedValue);
  };

  const handleCountryChange = () => {
    const inputElement = document.getElementById("phone-input");
    if (inputElement) {
      inputElement.focus();
    }
  };

  const errorFieldRefs = {
    email: emailRef,
    confirmEmail: confirmEmailRef,
    firstName: firstNameRef,
    lastName: lastNameRef,
    phoneNumber: phoneNumberRef,
    permissionLevel: permissionLevelRef,
    ward: wardRef,
    role: roleRef,
  };

  const scrollToFirstError = () => {
    const errorFields = Object.keys(formik.errors);

    const errorRef = errorFieldRefs[errorFields[0] as IErrorField];

    if (errorRef?.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (formik.errors && Object.keys(formik.errors).length > 0) {
      scrollToFirstError();
    }
  }, [formik.isSubmitting]);

  const permissionsDropdown = useMemo(() => {
    if (!isMounted) return null;

    const permissionOptions: IOption[] = [
      {
        label: wardLabel.singular,
        value: "hospital",
        key: uuidv4(),
      },
    ];

    if (canGrantHospitalGroupScope) {
      permissionOptions.push({
        label: t("settings_staffModal_hospitalGroup"),
        value: "hospital-group",
        key: uuidv4(),
      });
    }

    return (
      <SelectDropdown
        options={permissionOptions}
        width={188}
        height={42}
        placeholder={t("settings_staffModal_permissionLevel")}
        value={formik.values.permissionLevel}
        onValueChange={(value: string | string[]) => {
          if (value === "hospital-group") {
            formik.setFieldValue("ward", "");
            formik.setFieldTouched("ward", false);
          }

          formik.setFieldValue("permissionLevel", value.toString());
        }}
      />
    );
  }, [content, isMounted, wardLabel.singular, canGrantHospitalGroupScope, t]);

  const wardsDropdown = useMemo(() => {
    if (!isMounted) return null;

    const tempWards: IOption[] = [];

    if (availableWards) {
      availableWards.forEach((ward) => {
        tempWards.push({
          label: ward.wardName,
          value: ward.wardId.toString(),
          key: uuidv4(),
        });
      });
    }

    return (
      <SelectDropdown
        options={tempWards}
        width={188}
        height={42}
        placeholder={wardLabel.singular}
        value={formik.values.ward}
        onValueChange={(value: string | string[]) => {
          formik.setFieldValue("ward", value.toString());
        }}
      />
    );
  }, [availableWards, isMounted, wardLabel.singular]);

  // EN-4961: new Role dropdown. All six codes are listed so a hospital can
  // assign LegacyAdmin/Legacy/SystemController today and (later) the three
  // HCP roles once Phase 2 wires their permissions on the API side.
  // EN-5030: production still shows only Legacy/Legacy Admin, while UAT now
  // hides the legacy roles to push the new role model there.
  // Staging/development/test continue to show all six.
  const roleDropdown = useMemo(() => {
    if (!isMounted) return null;

    const allRoleOptions: IOption[] = [
      {
        label: t("settings_staffModal_roles_firstLineHcp"),
        value: "first-line-hcp",
        key: uuidv4(),
      },
      {
        label: t("settings_staffModal_roles_secondLineHcp"),
        value: "second-line-hcp",
        key: uuidv4(),
      },
      {
        label: t("settings_staffModal_roles_healthcareAdmin"),
        value: "healthcare-admin",
        key: uuidv4(),
      },
      {
        label: t("settings_staffModal_roles_systemController"),
        value: "system-controller",
        key: uuidv4(),
      },
      {
        label: t("settings_staffModal_roles_legacy"),
        value: "legacy",
        key: uuidv4(),
      },
      {
        label: t("settings_staffModal_roles_legacyAdmin"),
        value: "legacy-admin",
        key: uuidv4(),
      },
    ];

    const legacyRoleValues = new Set(["legacy", "legacy-admin"]);
    let roleOptions = allRoleOptions;
    if (isProduction()) {
      roleOptions = allRoleOptions.filter((o) => legacyRoleValues.has(o.value));
    } else if (isUat()) {
      roleOptions = allRoleOptions.filter(
        (o) => !legacyRoleValues.has(o.value)
      );
    }

    return (
      <SelectDropdown
        options={roleOptions}
        width={188}
        height={42}
        placeholder={t("settings_staffModal_role")}
        // Pass undefined (not the "" formik default) so antd renders the
        // "Role" placeholder in the empty state instead of a blank box.
        value={formik.values.role || undefined}
        onValueChange={(value: string | string[]) => {
          formik.setFieldValue("role", value.toString());
        }}
      />
    );
  }, [isMounted, formik.values.role, t]);

  if (isLoadingData) {
    return (
      <ModalContainer>
        <div style={{ marginBottom: "40px" }}>
          <SkeletonLoading height="22px" width="100px" />
        </div>

        <SkeletonLoading height="370px" />

        <div style={{ marginTop: "26px", display: "flex", gap: "24px" }}>
          <SkeletonLoading height="42px" width="186px" />
          <SkeletonLoading height="42px" width="100px" />
        </div>
      </ModalContainer>
    );
  }

  // Wards picker only shows when scope is "hospital" (i.e. single hospital).
  // Hospital-group and hierarchy scopes don't take a per-ward refinement.
  const isHospitalScope = formik.values.permissionLevel === "hospital";

  return (
    <ModalContainer>
      <ModalTitle>
        {staffId
          ? t("settings_staffModal_titleEdit")
          : t("settings_staffModal_titleAdd")}
      </ModalTitle>

      {!rerender ? (
        <ContentContainer>
          <Row>
            <FormField ref={emailRef}>
              <p>{t("settings_staffModal_emailAddress")}</p>
              <TextField
                label=""
                name="email"
                placeholder={t("settings_staffModal_emailAddress")}
                value={formik.values.email}
                onChange={formik.handleChange}
                backgroudColor="white"
                className={inputCss()}
                errorMessage={shouldShowErrorMessage("email")}
              />
            </FormField>

            <FormField ref={confirmEmailRef}>
              <p>{t("settings_staffModal_confirmEmail")}</p>
              <TextField
                label=""
                name="confirmEmail"
                placeholder={t("settings_staffModal_emailAddress")}
                value={formik.values.confirmEmail}
                onChange={formik.handleChange}
                backgroudColor="white"
                className={inputCss()}
                errorMessage={shouldShowErrorMessage("confirmEmail")}
                onPaste={(e) => e.preventDefault()}
              />
            </FormField>

            <FormField ref={firstNameRef}>
              <p>{t("settings_staffModal_firstName")}</p>
              <TextField
                label=""
                name="firstName"
                placeholder={t("settings_staffModal_firstName")}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                backgroudColor="white"
                className={inputCss()}
                errorMessage={shouldShowErrorMessage("firstName")}
              />
            </FormField>

            <FormField ref={lastNameRef}>
              <p>{t("settings_staffModal_lastName")}</p>
              <TextField
                label=""
                name="lastName"
                placeholder={t("settings_staffModal_lastName")}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                backgroudColor="white"
                className={inputCss()}
                errorMessage={shouldShowErrorMessage("lastName")}
              />
            </FormField>
          </Row>

          {/*
            EN-4961: Scope applies to every role — it bounds the user to a
            hospital (single ward), a hospital group, or the whole hierarchy.
            The Wards picker only appears when Scope is "hospital", because
            "Hospital Group" / "Hospital Group Hierarchy" already imply a
            wider set so a per-ward picker would be meaningless.
          */}
          <Row>
            <FormField ref={roleRef}>
              <p>{t("settings_staffModal_role")}</p>
              {roleDropdown}
              <ErrorMessage>{shouldShowErrorMessage("role")}</ErrorMessage>
            </FormField>

            <FormField ref={permissionLevelRef}>
              <p>{t("settings_staffModal_scope")}</p>
              {permissionsDropdown}
              <ErrorMessage>
                {shouldShowErrorMessage("permissionLevel")}
              </ErrorMessage>
            </FormField>

            {isHospitalScope && (
              <FormField ref={wardRef}>
                <p>{wardLabel.singular}</p>
                {wardsDropdown}
                <ErrorMessage>{shouldShowErrorMessage("ward")}</ErrorMessage>
              </FormField>
            )}

            <FormField ref={phoneNumberRef}>
              <p>{t("settings_staffModal_phoneNumber")}</p>
              <PhoneInput
                inputProps={{
                  autoFocus: true,
                  id: "phone-input",
                }}
                searchPlaceholder={t("settings_staffModal_search")}
                country={countryCode}
                containerClass={reactPhoneInputCss()}
                enableSearch
                value={phoneWithCountryCode}
                onChange={(value, country, e, formattedValue) => {
                  handleCountryChange();
                  handlePhoneInput(country, formattedValue);
                }}
                countryCodeEditable={false}
              />
              <ErrorMessage>
                {shouldShowErrorMessage("phoneNumber")}
              </ErrorMessage>
            </FormField>
          </Row>

          {!hideLegacyFields && (
            <Row>
              <CheckboxContainer alignItemsStart>
                <Checkbox
                  checked={formik.values.receiveAlerts}
                  label=""
                  onChange={(isChecked) => {
                    formik.setFieldValue("receiveAlerts", isChecked);

                    if (isChecked) {
                      mixpanelActions.track("User action: EnableAlerts");
                    } else {
                      mixpanelActions.track("User action: DisableAlerts");
                    }
                  }}
                />
                <p>
                  <b>{t("settings_staffModal_receiveAlertsTitle")}</b>{" "}
                  {t("settings_staffModal_receiveAlertsBody")}
                </p>
              </CheckboxContainer>
            </Row>
          )}

          {!hideLegacyFields && (
            <Row>
              <CheckboxContainer>
                <Checkbox
                  checked={formik.values.archiveMeasurements}
                  label=""
                  onChange={(isChecked) => {
                    formik.setFieldValue("archiveMeasurements", isChecked);

                    if (isChecked) {
                      mixpanelActions.track(
                        "User action: EnableArchiveMeasurements"
                      );
                    } else {
                      mixpanelActions.track(
                        "User action: DisableArchiveMeasurements"
                      );
                    }
                  }}
                />
                <p>
                  <b>{t("settings_staffModal_archiveMeasurementsTitle")} </b>
                  {!formik.values.archiveMeasurements
                    ? t("settings_staffModal_archiveMeasurementsOn")
                    : t("settings_staffModal_archiveMeasurementsOff")}
                </p>
              </CheckboxContainer>
            </Row>
          )}

          <Row>
            <CheckboxContainer>
              <Checkbox
                checked={formik.values.activeAccount}
                label=""
                onChange={(isChecked) => {
                  formik.setFieldValue("activeAccount", isChecked);

                  if (isChecked) {
                    mixpanelActions.track("User action: SetStaffActive");
                  } else {
                    mixpanelActions.track("User action: SetStaffInactive");
                  }
                }}
              />
              <p>
                <b>{t("settings_staffModal_activeAccountTitle")}</b>{" "}
                {t("settings_staffModal_activeAccountBody")}
              </p>
            </CheckboxContainer>
          </Row>

          {/*
            EN-5014: the legacy "Admin account" checkbox was removed. The Role
            dropdown (Legacy Admin) is now the single source of truth for admin
            status — the backend derives the legacy is_admin flag from the role.
          */}
        </ContentContainer>
      ) : null}

      <ServerErrorMessage>{errorMessage}</ServerErrorMessage>

      <SubmitContainer>
        <Button
          label={t("settings_staffModal_save")}
          type="submit"
          onClick={formik.submitForm}
          isLoading={isLoading}
          className={submitButtonCss()}
        />

        <CancelButton
          onClick={() => {
            mixpanelActions.track("User action: Cancel");
            onClose();
          }}
          style={{ marginLeft: "-35px", padding: "0 30px" }}
        >
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}
